/**
 * F-092 — Build version badge.
 *
 * 우하단 모서리에 작은 반투명 배지로 `vX.Y.Z·<sha>` 표시. 사용자가
 * "내가 보고 있는 게 정말 새 빌드인가?" 를 한눈에 확인하게 해 디버깅
 * cycle 을 단축. 탭하면 build time 까지 펼쳐서 토스트로 보여준다.
 *
 * Vite `define` 으로 주입된 `__APP_VERSION__` / `__APP_COMMIT__` /
 * `__APP_BUILD_TIME__` 글로벌을 사용 — 빌드 타임에 인라인되므로
 * runtime fetch 없이 즉시 표시.
 */

export interface VersionBadgeOptions {
  version?: string;
  commit?: string;
  buildTime?: string;
  /** Test seam — DOM root for mounting (default: document.body). */
  root?: HTMLElement;
  /**
   * F-091 진단 — 토스트에 audio 시스템 상태를 함께 표시. iPad 사용자가
   * 직접 디바이스에서 실시간 ctx 상태/sample rate 를 볼 수 있도록.
   */
  diagnose?: () => string;
}

export function mountVersionBadge(
  options: VersionBadgeOptions = {},
): { detach: () => void } {
  const version = options.version ?? readBuildGlobal("version");
  const commit = options.commit ?? readBuildGlobal("commit");
  const buildTime = options.buildTime ?? readBuildGlobal("time");
  const root = options.root ?? document.body;

  const badge = document.createElement("button");
  badge.type = "button";
  badge.setAttribute("data-testid", "version-badge");
  badge.setAttribute("aria-label", `Build v${version}, commit ${commit}`);
  badge.textContent = `v${version}·${commit}`;
  badge.className = [
    "fixed",
    "bottom-2",
    "right-2",
    "z-40",
    "px-2",
    "py-0.5",
    "rounded",
    "bg-slate-900/40",
    "text-slate-400/80",
    "text-[10px]",
    "font-mono",
    "leading-none",
    "tracking-tight",
    "backdrop-blur-sm",
    "border",
    "border-slate-700/40",
    "hover:bg-slate-900/70",
    "hover:text-slate-200",
    "transition",
    "select-none",
    "pointer-events-auto",
  ].join(" ");

  let toast: HTMLDivElement | null = null;
  let toastTimer: number | undefined;

  const showToast = (): void => {
    if (toast) toast.remove();
    toast = document.createElement("div");
    toast.setAttribute("data-testid", "version-badge-toast");
    const diag = options.diagnose?.() ?? "";
    toast.textContent = formatToast(version, commit, buildTime, diag);
    toast.className = [
      "fixed",
      "bottom-9",
      "right-2",
      "z-40",
      "px-3",
      "py-2",
      "rounded-md",
      "bg-slate-900/95",
      "text-slate-100",
      "text-xs",
      "font-mono",
      "shadow-xl",
      "border",
      "border-slate-700",
      "whitespace-pre",
      "leading-relaxed",
      "pointer-events-none",
    ].join(" ");
    root.appendChild(toast);
    if (toastTimer !== undefined) window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast?.remove();
      toast = null;
    }, 3500);
  };

  badge.addEventListener("click", showToast);
  root.appendChild(badge);

  return {
    detach: () => {
      badge.remove();
      toast?.remove();
      if (toastTimer !== undefined) window.clearTimeout(toastTimer);
    },
  };
}

/**
 * Vite `define` 은 식별자 텍스트 치환 — 빌드 타임에 `__APP_VERSION__`
 * 토큰이 `"0.1.0"` 같은 리터럴로 인라인됨. test/dev 환경에선 식별자 자체가
 * 미정의라 ReferenceError 발생 → try/catch 로 fallback.
 */
function readBuildGlobal(kind: "version" | "commit" | "time"): string {
  try {
    if (kind === "version") return __APP_VERSION__;
    if (kind === "commit") return __APP_COMMIT__;
    return __APP_BUILD_TIME__;
  } catch {
    if (kind === "version") return "0.0.0";
    if (kind === "commit") return "dev";
    return "";
  }
}

function formatToast(
  version: string,
  commit: string,
  buildTime: string,
  diag: string,
): string {
  const lines = [`v${version}`, `commit ${commit}`];
  if (buildTime) {
    lines.push(`built ${formatBuildTime(buildTime)}`);
  }
  if (diag) {
    lines.push(diag);
  }
  return lines.join("\n");
}

function formatBuildTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // 짧고 사람 친화적: 2026-04-26 16:50 UTC
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const HH = String(d.getUTCHours()).padStart(2, "0");
  const MM = String(d.getUTCMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${HH}:${MM} UTC`;
}
